import React, { useMemo } from 'react';
import moment from 'moment';
import i18n from '@i18n';
import { ErrorMessageOverlay, ExpirationIntroMessage, PlayButtonOverlay } from '@/player/VideoStreamNg';
import { getStreamErrorMessage } from 'nike-ui/src/components/streams/errors';
import VideoCountdown from 'nike-ui/src/components/video-countdown/views/VideoCountdown';
import { trackVideoStart } from 'nike-ui/src/analytics/events/video';
import { NikePlayerSource } from 'nike-ui/src/components/streams/players/videojs/types';
import { NikeStream } from 'nike-ui/src/types/generated/nike-stream';
import { StreamingModuleState } from 'nike-ui/src/components/streams/cms-streaming-module';
import { NikePlayer } from 'nike-ui/src/components/streams/players/NikePlayer';
import { trackDwhVideoEvent } from 'nike-ui/src/components/dwh-tracking/api';

export const StreamingModuleVideo = ({
    matchId,
    startTime,
    sport,
    sportId,
    tournamentId,
    provider,
    moduleState,
    onRestart,
    src,
    expirationIntroDate
}: {
    src: string;
    matchId: string;
    startTime: string;
    sport: string;
    sportId: number;
    tournamentId?: string;
    provider: NikeStream.Provider;
    moduleState: StreamingModuleState;
    onRestart: () => void;
    expirationIntroDate: string;
}) => {
    const source: NikePlayerSource = useMemo(
        () => ({
            src: src,
            type: 'application/x-mpegurl',
            handleManifestRedirects: true,
            providerName: provider
        }),
        [src, provider]
    );

    if (moduleState.value === 'VALIDATING') {
        return <PlayButtonOverlay sportId={sport} onPlayClick={() => null} />;
    }

    if (startTime && moment(startTime).isAfter(moment())) {
        return <VideoCountdown startTime={startTime} restart={onRestart} />;
    }

    if (moduleState.value === 'INVALID') {
        const error = moduleState as any;
        const isLoggedError = error && error.code === 'USER_NOT_LOGGED_IN';
        return (
            <div className="mb-8">
                <ErrorMessageOverlay
                    sportId={sport}
                    message={getStreamErrorMessage(error.code, error.params)}
                    data-atid={error.code}
                    onRestartClick={onRestart}
                    isLoggedError={isLoggedError}
                    label={isLoggedError ? i18n('jsp.login.Prihlasit') : i18n('streams.restart')}
                />
            </div>
        );
    }

    const trackVideoData = (eventName: string, videoTime: number) => {
        if (eventName === 'video_start') {
            trackVideoStart({
                provider: provider,
                sportEventId: matchId,
                sport: sport,
                sportId: sportId,
                videoId: src
            });
        }
        trackDwhVideoEvent(eventName, src, provider, videoTime, matchId, sportId?.toString(), tournamentId, null);
    };

    return (
        <div className="mb-8">
            <ExpirationIntroMessage expirationIntroDate={expirationIntroDate} />
            <div className={'default-sport'}>
                <NikePlayer className="markdown-video" source={source} trackVideoData={trackVideoData} />
            </div>
        </div>
    );
};
